import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ShieldAlert, AlertTriangle } from 'lucide-react';

/**
 * Emergency admin sign-in — /emergency-access
 *
 * NOT linked from anywhere in the UI. Bookmarked by the break-glass admin in
 * their password manager alongside the credential. Every successful sign-in
 * on this route:
 *   - Fires a distinctive audit log entry (source=emergency_access)
 *   - Sends an alert email to BREAK_GLASS_ALERT_EMAIL (if configured)
 *   - Yields a short-lived (2h) JWT instead of the normal 8h
 *   - Is rate-limited to 5 attempts per IP per 15min
 *
 * Regular users hitting this URL by accident see a clear warning and no
 * pathway forward without valid break-glass credentials.
 */
export default function EmergencyAccess() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      if (err.response?.status === 429) {
        setError(err.response?.data?.detail || 'Too many failed attempts. Wait 15 minutes.');
      } else if (err.response?.status === 403) {
        setError('This account cannot use emergency access. Sign in with Microsoft instead.');
      } else if (err.response) {
        setError(err.response.data?.detail || 'Sign-in failed.');
      } else {
        setError('Network error. Please check your connection.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="theme-pin-light min-h-screen flex items-center justify-center bg-slate-950 px-4" data-testid="emergency-access-page">
      <div className="w-full max-w-md">
        {/* Distinctive amber warning banner at the top so nobody mistakes this for the normal login */}
        <div className="mb-4 flex items-center gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-amber-100">
          <ShieldAlert className="h-5 w-5 flex-shrink-0" />
          <div className="text-sm">
            <div className="font-semibold">Emergency Access Only</div>
            <div className="text-amber-200/80 text-xs">
              Use this form only when Microsoft sign-in is unavailable. Every use is logged and reported.
            </div>
          </div>
        </div>

        <Card className="border-amber-500/30 bg-slate-900 shadow-2xl shadow-amber-900/20">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-bold text-center text-amber-100">
              Emergency Admin Sign-In
            </CardTitle>
            <CardDescription className="text-center text-slate-400">
              For authorized administrators only.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="bg-red-950 border-red-800 text-red-100 mb-4" data-testid="emergency-login-error">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-200">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="username"
                  placeholder="admin@tricorps.com"
                  data-testid="emergency-email-input"
                  className="h-11 bg-slate-800 border-slate-700 text-slate-100 focus:border-amber-500"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-200">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  data-testid="emergency-password-input"
                  className="h-11 bg-slate-800 border-slate-700 text-slate-100 focus:border-amber-500"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-11 bg-amber-600 hover:bg-amber-500 text-slate-950 font-semibold"
                disabled={loading}
                data-testid="emergency-submit-btn"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="mt-6 text-center text-xs text-slate-500 space-y-1">
              <div>Session expires in 2 hours.</div>
              <div>
                <a href="/login" className="underline hover:text-slate-300" data-testid="back-to-normal-login">
                  Back to normal sign-in
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
